import React, { useState, useMemo } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  Typography,
  Box,
  Link,
} from "@mui/material"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"

const COLUMNS = [
  { id: "templeName", label: "Temple", sortKey: (r) => r.templeName, numeric: false },
  { id: "templeLocation", label: "Location", sortKey: (r) => r.templeLocation || "", numeric: false },
  { id: "aaMi", label: "Closest AA Airport", sortKey: (r) => r.aaMi, numeric: true, airport: "aaAirport", distance: "aaMi" },
  { id: "zedMi", label: "Closest ZED Airport", sortKey: (r) => r.zedMi, numeric: true, airport: "zedAirport", distance: "zedMi" },
  { id: "relativeMi", label: "Closest Person", sortKey: (r) => r.relativeMi, numeric: true, relative: true },
]

function fmtMi(mi) {
  if (mi == null) return "—"
  if (mi < 10) return `${mi.toFixed(1)} mi`
  return `${Math.round(mi)} mi`
}

function compareRows(a, b, column, dir) {
  const av = column.sortKey(a)
  const bv = column.sortKey(b)
  // Nulls last regardless of direction
  if (av == null && bv == null) return 0
  if (av == null) return 1
  if (bv == null) return -1
  if (column.numeric) return dir === "asc" ? av - bv : bv - av
  const cmp = String(av).localeCompare(String(bv))
  return dir === "asc" ? cmp : -cmp
}

function TempleTable({ rows }) {
  const [sortBy, setSortBy] = useState("templeName")
  const [sortDir, setSortDir] = useState("asc")

  const handleSort = (id) => {
    if (sortBy === id) {
      setSortDir(sortDir === "asc" ? "desc" : "asc")
    } else {
      setSortBy(id)
      setSortDir("asc")
    }
  }

  const sorted = useMemo(() => {
    const column = COLUMNS.find((c) => c.id === sortBy)
    if (!column) return rows
    return [...rows].sort((a, b) => compareRows(a, b, column, sortDir))
  }, [rows, sortBy, sortDir])

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell key={col.id} align={col.numeric ? "right" : "left"} sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortBy === col.id}
                  direction={sortBy === col.id ? sortDir : "asc"}
                  onClick={() => handleSort(col.id)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => (
            <TableRow key={row.templeName} hover>
              <TableCell>{row.templeName}</TableCell>
              <TableCell>{row.templeLocation || "—"}</TableCell>
              <AirportCell airport={row.aaAirport} mi={row.aaMi} />
              <AirportCell airport={row.zedAirport} mi={row.zedMi} />
              <TableCell align="right">
                {row.relative ? (
                  <Tooltip
                    arrow
                    placement="left"
                    enterDelay={150}
                    title={<PersonTooltipContent person={row.relative} />}
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 320,
                          bgcolor: "background.paper",
                          color: "text.primary",
                          border: 1,
                          borderColor: "divider",
                          boxShadow: 3,
                          p: 1.25,
                        },
                      },
                      arrow: { sx: { color: "background.paper", "&::before": { border: 1, borderColor: "divider" } } },
                    }}
                  >
                    <span style={{ cursor: "default" }}>
                      <Typography component="span" variant="body2">
                        {row.relative.name}
                      </Typography>
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        {fmtMi(row.relativeMi)}
                      </Typography>
                    </span>
                  </Tooltip>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function PersonTooltipContent({ person }) {
  const cityState = [person.city, person.state].filter(Boolean).join(", ")
  const cityStateZip = [cityState, person.zip].filter(Boolean).join(" ")
  const hasCoords = typeof person.lat === "number" && typeof person.lon === "number"
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {person.name}
      </Typography>
      {person.category && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {person.category}
        </Typography>
      )}
      {person.address && (
        <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
          {person.address}
        </Typography>
      )}
      {person.address2 && (
        <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
          {person.address2}
        </Typography>
      )}
      {cityStateZip && (
        <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
          {cityStateZip}
        </Typography>
      )}
      {hasCoords && (
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Stored coords: {person.lat.toFixed(4)}, {person.lon.toFixed(4)}
          </Typography>
          <Link
            href={`https://www.google.com/maps?q=${person.lat},${person.lon}`}
            target="_blank"
            rel="noreferrer"
            variant="caption"
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, mt: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            Open in Google Maps <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        </Box>
      )}
    </Box>
  )
}

function AirportCell({ airport, mi }) {
  if (!airport) return <TableCell align="right">—</TableCell>
  return (
    <TableCell align="right">
      <Tooltip title={`${airport.name}${airport.city ? ` — ${airport.city}` : ""}`} placement="left">
        <span>
          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
            {airport.iata}
          </Typography>
          <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
            {fmtMi(mi)}
          </Typography>
        </span>
      </Tooltip>
    </TableCell>
  )
}

export default TempleTable
